import { cn } from '@noorden-insurtech/alba-design-system';
import { ClipboardList, FileSearch, ListChecks, Loader } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { InspectionsSummaryResponse } from '@/services/inspections/types';
import { InspectionStatus } from '@/shared/enums/inspection-status.enum';
import { useInspectionsSummary } from '../hooks';

type SummaryKey = keyof InspectionsSummaryResponse;

interface CardConfig {
  apiStatus: 'screening' | InspectionStatus;
  summaryKey: SummaryKey;
  label: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  activeBorder: string;
}

const CARD_CONFIG: CardConfig[] = [
  {
    apiStatus: 'screening',
    summaryKey: 'screening',
    label: 'Triagem',
    icon: ListChecks,
    iconColor: 'text-alba-green-600',
    iconBg: 'bg-alba-green-50',
    activeBorder: 'border-alba-green-600',
  },
  {
    apiStatus: InspectionStatus.Requested,
    summaryKey: 'requested',
    label: 'Inspeções Solicitadas',
    icon: ClipboardList,
    iconColor: 'text-alba-green-600',
    iconBg: 'bg-alba-green-50',
    activeBorder: 'border-alba-green-600',
  },
  {
    apiStatus: InspectionStatus.InProgress,
    summaryKey: 'inProgress',
    label: 'Inspeções em andamento',
    icon: Loader,
    iconColor: 'text-alba-green-600',
    iconBg: 'bg-alba-green-50',
    activeBorder: 'border-alba-green-600',
  },
  {
    apiStatus: InspectionStatus.UnderReview,
    summaryKey: 'underReview',
    label: 'Inspeções Em analise',
    icon: FileSearch,
    iconColor: 'text-alba-green-600',
    iconBg: 'bg-alba-green-50',
    activeBorder: 'border-alba-green-600',
  },
];

type InspectionStatusCardsProps = {
  activeFilter: string;
  onFilterChange: (_status: string) => void;
  /** Filtros da listagem repassados para os contadores não contradizerem a lista. */
  productCode?: string;
  proposalDateFrom?: string;
  proposalDateTo?: string;
  partyCode?: string;
};

export function InspectionStatusCards({
  activeFilter,
  onFilterChange,
  productCode,
  proposalDateFrom,
  proposalDateTo,
  partyCode,
}: InspectionStatusCardsProps) {
  const { data, isLoading } = useInspectionsSummary({
    productCode,
    proposalDateFrom,
    proposalDateTo,
    partyCode,
  });

  const handleCardClick = (apiStatus: string) => {
    onFilterChange(activeFilter === apiStatus ? '' : apiStatus);
  };

  return (
    <div className='grid w-full grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 lg:gap-4'>
      {CARD_CONFIG.map(card => {
        const count = data?.[card.summaryKey] ?? 0;
        const isActive = activeFilter === card.apiStatus;
        const Icon = card.icon;

        return (
          <button
            key={card.apiStatus}
            type='button'
            onClick={() => handleCardClick(card.apiStatus)}
            className={cn(
              'flex min-h-[102px] cursor-pointer flex-col rounded-2xl border px-3 py-3 text-left transition-all lg:min-h-[132px] lg:px-4',
              isActive
                ? `border-2 ${card.activeBorder} bg-white shadow-sm`
                : 'border-alba-gray-200 bg-white hover:border-alba-gray-300 hover:shadow-sm'
            )}
          >
            <div className='flex items-start justify-between gap-3'>
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg lg:h-9 lg:w-9',
                  card.iconBg
                )}
              >
                <Icon
                  className={cn('h-5 w-5', card.iconColor)}
                  strokeWidth={1.6}
                />
              </div>
              <div className='flex min-h-9 items-center justify-end'>
                {isLoading ? (
                  <span className='inline-block h-7 w-10 animate-pulse rounded bg-gray-200' />
                ) : (
                  <span className='font-montserrat text-3xl font-bold leading-none text-alba-black-500 tabular-nums lg:text-2xl'>
                    {count}
                  </span>
                )}
              </div>
            </div>
            <span className='mt-auto font-montserrat text-xs leading-tight text-alba-gray-600 lg:text-sm'>
              {card.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
